import { useContext, useMemo } from 'react';
import { UserContext } from '@/contexts/UserContext';
import { ASSET_HEAVY } from '@/constants/featureFlags';

/**
 * Gates visibility of external accounting-integration columns on the invoice tables — NetSuite today,
 * and future integrations (OpenForce, Bill.com, ...) as they land. New integration columns should gate
 * on this single flag so they inherit the same access rules automatically.
 *
 * Visible to:
 *   - Onward org admins (ADMIN role scoped to an organization, not impersonating a client)
 *   - Onward admins (ONWARD_ADMIN role)
 *   - Asset-heavy carrier admins (CARRIER role + ASSET_HEAVY circle), excluding non-admin/-invoicing teammates
 */
export const useCanViewIntegrations = () => {
    const { user, client, teammate } = useContext(UserContext);

    return useMemo(() => {
        const isAdmin = user?.roles?.['ADMIN'] && !client?.client_id && user?.organization_id;
        const isOnwardAdmin = user?.roles?.['ONWARD_ADMIN'] && !client?.client_id;
        const isCarrierAdmin =
            client?.user?.roles?.['CARRIER'] &&
            client?.user?.circles?.[ASSET_HEAVY] &&
            (!teammate?.teammate_id || ['admin', 'invoicing'].some((role) => teammate?.roles?.[role]));
        return Boolean(isAdmin || isOnwardAdmin || isCarrierAdmin);
    }, [user, client, teammate]);
};
